import { Alert, Divider, Link, Paper, Typography, useMediaQuery } from '@mui/material';
import SixCodeTextField from 'components/SixCodeTextField/SixCodeTextField';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useResendVerificationCode from 'hooks/useResendVerificationCode/useResendVerificationCode';
import useVerifyCode from 'hooks/useVerifyCode/useVerifyCode';
import React, { useRef, useState } from 'react';
import Background from 'ui/atoms/Background/Background';
import Loader from 'ui/atoms/Loader/Loader';
import ComponentWrapper from '../ComponentWrapper/ComponentWrapper';

export interface Props {
  email?: string | null;
}

const VerifyAccountPage: React.FC<React.PropsWithChildren<Props>> = ({ email }) => {
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });

  const {
    verifyCognitoUser,
    loading: verifyCognitoUserLoading,
    error: verifyCognitoUserError
  } = useVerifyCode({ handleFetchError });

  const { resendVerificationCode, loading: resendVerificationCodeLoading } = useResendVerificationCode({
    handleFetchError
  });

  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  if (inputRefs.current.length === 0) {
    inputRefs.current = Array(6).fill(null);
  }

  const onVerifyCognitoUser = async (email: string | undefined | null, code: string) => {
    if (email && !verifyCognitoUserLoading) {
      const { data } = await verifyCognitoUser(email, code);
      return data;
    }
  };

  const onResendCode = async () => {
    if (email && !resendVerificationCodeLoading) {
      resendVerificationCode(email);
    }
  };

  const isExtraSmallScreen = useMediaQuery('(min-width: 0px) and (max-width:627px)');
  const isSmallScreen = useMediaQuery('(min-width: 628px) and (max-width:643px)');
  const isMediumScreen = useMediaQuery('(min-width: 644px) and (max-width:663px)');

  const getPaperPadding = () => {
    if (isExtraSmallScreen || isSmallScreen) return '48px 24px';
    if (isMediumScreen) return '48px 40px';
    return '48px 80px';
  };

  const getPaperWidth = () => {
    if (isExtraSmallScreen) {
      return 'calc(100vw - 80px)';
    }
    if (isSmallScreen) {
      return '520px';
    }
    if (isMediumScreen) {
      return '552px';
    }
    return '632px';
  };

  return (
    <ComponentWrapper width="100vw" height="100vh" justifyContent="center" alignItems="center">
      <Background id="background" />
      <Paper
        elevation={3}
        data-testid="login-window"
        id="login-window"
        style={{
          alignSelf: 'center',
          justifySelf: 'center',
          padding: getPaperPadding(),
          width: getPaperWidth(),
          borderRadius: '10px'
        }}
      >
        <ComponentWrapper flexDirection="column" gap="16px">
          <Typography variant="h4">Verify administrator account</Typography>
          <Divider />
          <Typography variant="body1">
            We have send the verification code to {email}. Do not close this screen until you have entered the code.
          </Typography>
          <ComponentWrapper flexDirection="column" gap="16px">
            <Typography variant="h5">Verification code</Typography>
            <ComponentWrapper flexDirection="row" gap="18px">
              <SixCodeTextField
                code={code}
                setCode={setCode}
                setError={setError}
                triggerWhenCodeIsFull={() => onVerifyCognitoUser(email, code.join(''))}
              />
            </ComponentWrapper>
            <ComponentWrapper flexDirection="column">
              <Typography variant="body2" color="secondary">
                No email in inbox or spam after a few minutes?
              </Typography>
              <Link onClick={onResendCode} style={{ cursor: 'pointer' }}>
                Resend code
              </Link>
            </ComponentWrapper>
          </ComponentWrapper>
          <ComponentWrapper>
            {verifyCognitoUserLoading && (
              <ComponentWrapper width="100%" justifyContent="center" alignItems="center" flexDirection="column">
                <Loader size={48} />
                <Typography variant="body2" color="secondary">
                  Checking code...
                </Typography>
              </ComponentWrapper>
            )}
            {(error || verifyCognitoUserError) && (
              <Alert
                variant="standard"
                severity={'error'}
                style={{ backgroundColor: 'transparent', border: '1px solid #D32F2F' }}
                sx={{
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  '& .MuiSvgIcon-root': {
                    color: '#F44336'
                  },
                  '& .MuiAlert-message': {
                    color: '#5F2120'
                  }
                }}
              >
                <ComponentWrapper flexDirection="column">
                  <Typography color="#5F2120" variant="h6">
                    Invalid verification code
                  </Typography>
                  <Typography variant="body2" color="#5F2120">
                    Please check your email for the correct code, or click 'Resend Code' to receive a new one.
                  </Typography>
                </ComponentWrapper>
              </Alert>
            )}
          </ComponentWrapper>
        </ComponentWrapper>
      </Paper>
    </ComponentWrapper>
  );
};

export default VerifyAccountPage;
